import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {USER_API_BASE} from "../../../../utils/apiConfig";

/* ---------------------------- Constants --------------------------- */

const POLL_INTERVAL_MS = 1000; // Delay between two status checks
const MAX_POLL_ATTEMPTS = 60; // Give up after ~60 seconds

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/* ---------------------------- Component --------------------------- */

/**
 * Props:
 * - config: { code, language, editable, showCompiler, showCopyButton }
 * - userId: used to build the API route /:user_id/project
 */
const ProgramCodeBlock = ({ config, userId }) => {
    const {
        code: initialCode = "",
        language = "text",
        editable = false,
        showCompiler = false,
        showCopyButton = false,
    } = config || {};

    // Current code (may differ from the initial code when editable)
    const [code, setCode] = useState(initialCode);
    const [isRunning, setIsRunning] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    // Prevents state updates (and further polling) after the component unmounts
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // Keep the editor in sync if a new config is passed in
    useEffect(() => {
        setCode(initialCode);
    }, [initialCode]);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(code);
        } catch (err) {
            console.error("Failed to copy code:", err);
        }
    };

    /** Step 1: enqueue the job and get back its id */
    const enqueueJob = async () => {
        const { data } = await axios.post(`${USER_API_BASE}/${userId}/project`, {
            code,
            language,
            stdin: "",
        });
        return data.jobId;
    };

    /**
     * Step 2: poll the job until it is completed or failed.
     * Intermediate states (waiting / active / delayed) simply trigger another check.
     * Returns null if the component unmounted while polling.
     */
    const pollJobResult = async (jobId) => {
        for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
            if (!isMountedRef.current) return null;

            const { data } = await axios.get(
                `${USER_API_BASE}/${userId}/project/${jobId}`
            );

            if (data.status === "completed" || data.status === "failed") {
                return data;
            }

            await sleep(POLL_INTERVAL_MS);
        }

        throw new Error("Execution timed out");
    };

    const handleRun = async () => {
        setIsRunning(true);
        setResult(null);
        setError(null);

        try {
            const jobId = await enqueueJob();
            const finalResult = await pollJobResult(jobId);

            if (finalResult && isMountedRef.current) {
                setResult(finalResult);
            }
        } catch (err) {
            if (!isMountedRef.current) return;

            // Prefer the server's message (e.g. "Job not found") when available
            const message = axios.isAxiosError(err)
                ? err.response?.data?.message || err.message
                : err.message || "Something went wrong";

            setError(message);
        } finally {
            if (isMountedRef.current) setIsRunning(false);
        }
    };

    /** Renders the job output depending on its final state */
    const renderResult = () => {
        if (error) {
            return <pre className="text-sm text-red-400 whitespace-pre-wrap">{error}</pre>;
        }

        if (!result) return null;

        if (result.status === "failed") {
            return (
                <pre className="text-sm text-red-400 whitespace-pre-wrap">
                    {result.message || "Execution failed"}
                </pre>
            );
        }

        // Completed: show stdout / stderr if present, otherwise the raw result
        const { status, stdout, stderr, ...rest } = result;
        const hasKnownOutput = Boolean(stdout || stderr);

        return (
            <div className="space-y-2">
                {stdout && (
                    <pre className="text-sm text-green-400 whitespace-pre-wrap">{stdout}</pre>
                )}
                {stderr && (
                    <pre className="text-sm text-red-400 whitespace-pre-wrap">{stderr}</pre>
                )}
                {!hasKnownOutput && (
                    <pre className="text-sm text-gray-300 whitespace-pre-wrap">
                        {JSON.stringify(rest, null, 2)}
                    </pre>
                )}
            </div>
        );
    };

    return (
        <div className="w-full overflow-hidden rounded-xl border border-gray-200 bg-gray-950">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-800 px-4 py-2">
                <span className="text-xs text-gray-400 font-mono">{language}</span>

                <div className="flex items-center gap-3">
                    {editable && (
                        <span className="text-xs text-gray-400">قابل ویرایش</span>
                    )}

                    {showCopyButton && (
                        <button
                            type="button"
                            onClick={handleCopy}
                            className="text-xs text-gray-400 transition hover:text-white"
                        >
                            کپی
                        </button>
                    )}
                </div>
            </div>

            {/* Code: textarea when editable, read-only block otherwise */}
            {editable ? (
                <textarea
                    dir="ltr"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    spellCheck={false}
                    rows={Math.max(code.split("\n").length, 6)}
                    className="w-full resize-y bg-transparent p-4 text-left font-mono text-sm leading-6 text-gray-100 outline-none"
                />
            ) : (
                <pre
                    dir="ltr"
                    className="overflow-x-auto p-4 text-left font-mono text-sm leading-6 text-gray-100"
                >
                    <code>{code}</code>
                </pre>
            )}

            {/* Compiler */}
            {showCompiler && (
                <div className="space-y-3 border-t border-gray-800 px-4 py-3">
                    <button
                        type="button"
                        onClick={handleRun}
                        disabled={isRunning}
                        className="rounded-md bg-green-600 px-3 py-1 text-xs text-white transition hover:bg-green-500 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {isRunning ? "در حال اجرا..." : "اجرا"}
                    </button>

                    {/* Output is always LTR since it comes from the program */}
                    <div dir="ltr" className="text-left font-mono">
                        {renderResult()}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProgramCodeBlock;